import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'

export type IntentMessageSentiment = 'positive' | 'neutral' | 'negative' | 'all'

export interface IntentMessage {
  conversationId: string
  createdAt: string | null
  externalUrl: string | null
  id: string
  matchedVariant: string | null
  messageCount: number
  operator: { id: string | null; name: string } | null
  platform: string | null
  sentiment: 'positive' | 'neutral' | 'negative'
  snippet: string
  user: { avatarUrl: string | null; id: string | null; name: string }
}

export interface TopIntentMessagesResponse {
  intent: string
  messages: IntentMessage[]
  nextCursor: string | null
  totalMessages: number
}

interface UseGetTopIntentMessagesParams {
  enabled?: boolean
  fromDate?: string
  intent: string
  limit?: number
  pageId: string
  sentiment?: IntentMessageSentiment
  toDate?: string
}

export const useGetTopIntentMessagesQuery = (
  params: UseGetTopIntentMessagesParams
) => {
  const limit = params.limit ?? 20

  return useInfiniteQuery<TopIntentMessagesResponse>({
    enabled: params.enabled !== false && !!params.pageId && !!params.intent,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await analyticsApi().get<TopIntentMessagesResponse>(
        '/v1_private/inbox-analysis/top-intents/messages',
        {
          params: {
            cursor: pageParam as string | undefined,
            fromDate: params.fromDate,
            intent: params.intent,
            limit,
            pageId: params.pageId,
            sentiment:
              params.sentiment && params.sentiment !== 'all'
                ? params.sentiment
                : undefined,
            toDate: params.toDate,
          },
        }
      )
      return data
    },
    queryKey: [
      QueryKey.GET_TOP_INTENT_MESSAGES,
      params.pageId,
      params.intent,
      params.fromDate,
      params.toDate,
      params.sentiment ?? 'all',
      limit,
    ],
  })
}
